import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  Link,
  Divider,
} from '@mui/material';
import { Gavel, Security, Description, Logout } from '@mui/icons-material';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';

const TermsAcceptancePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout } = useAuth();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  // True when this person already accepted an older version and the terms
  // changed since: the screen then says so instead of "set up your account".
  const [updatedTerms, setUpdatedTerms] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const response = await makeApiCall('/onboarding/status', { headers });
        if (!response.ok) return;
        const body = await response.json();
        if (!cancelled) setUpdatedTerms(body?.data?.termsReacceptanceRequired === true);
      } catch {
        // Not a returning user, or offline: the ordinary first-time screen shows.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const canContinue = termsAccepted && privacyAccepted;

  const handleContinue = async () => {
    if (!canContinue) {
      setError('Please accept both the Terms of Service and Privacy Policy to continue');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/onboarding/accept-terms', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          termsAccepted: true,
          privacyAccepted: true,
          acceptedAt: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error?.message || 'Failed to accept terms');
      }

      const result = await response.json();
      let nextStep = result.data?.nextStep || '/onboarding/select-type';
      const claimBrand = searchParams.get('brand');
      if (claimBrand && nextStep.startsWith('/onboarding/setup-vendor')) {
        const separator = nextStep.includes('?') ? '&' : '?';
        nextStep = `${nextStep}${separator}brand=${encodeURIComponent(claimBrand)}`;
      }
      const inviteToken = searchParams.get('invite');
      if (inviteToken && nextStep.startsWith('/onboarding/setup-vendor')) {
        const separator = nextStep.includes('?') ? '&' : '?';
        nextStep = `${nextStep}${separator}invite=${encodeURIComponent(inviteToken)}`;
      }
      navigate(nextStep);
    } catch (err) {
      console.error('Error accepting terms:', err);
      setError(err instanceof Error ? err.message : 'Failed to accept terms. Please try again.');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    setError(null);
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Failed to log out:', err);
      setError('Failed to log out. Please try again.');
      setLoggingOut(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        pt: { xs: 4, md: 8 },
        pb: { xs: 4, md: 8 },
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="text"
            startIcon={<Logout />}
            onClick={handleLogout}
            disabled={loggingOut}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {loggingOut ? 'Signing out...' : 'Sign out'}
          </Button>
        </Box>
        <Box textAlign="center" mb={4}>
          <Gavel sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Terms &amp; Conditions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {updatedTerms
              ? 'We have updated our Terms of Service and Privacy Policy. Please review and accept them to keep using Motzu.'
              : 'Please review and accept our terms to continue with your account setup.'}
          </Typography>
        </Box>

        {updatedTerms && (
          <Alert severity="info" sx={{ mb: 3 }}>
            What changed: the Terms now cap our liability at the fees you paid in the last 12
            months, make a refund your sole remedy, and add an indemnification section; the
            Privacy Policy adds rights for residents of the EU, EEA and UK (GDPR). Read the full{' '}
            <Link href="/terms" target="_blank" rel="noopener">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href="/privacy" target="_blank" rel="noopener">
              Privacy Policy
            </Link>
            .
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Description color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Terms of Service
              </Typography>
            </Box>
            <Box
              sx={{
                maxHeight: 200,
                overflowY: 'auto',
                bgcolor: 'grey.50',
                p: 2,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" paragraph>
                <strong>1. Acceptance of Terms</strong>
                <br />
                By accessing and using the Motzu platform ("Service"), you agree to be bound by these
                Terms of Service. If you do not agree to these terms, please do not use our Service.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>2. Service Description</strong>
                <br />
                Motzu provides camera product data management, pricing information, and e-commerce
                integration services for authorized retailers and vendors in the photography equipment
                industry.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>3. User Responsibilities</strong>
                <br />
                You agree to: (a) provide accurate information during registration; (b) maintain the
                security of your account credentials; (c) comply with all applicable laws and
                regulations; (d) use the Service only for legitimate business purposes.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>4. Data Usage</strong>
                <br />
                Product data, pricing information, and other content provided through the Service is
                for your internal business use only. Redistribution or resale of this data without
                authorization is prohibited.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>5. MAP Compliance</strong>
                <br />
                Users agree to comply with Minimum Advertised Price (MAP) policies for all products
                where applicable. Violations may result in account suspension.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>6. Limitation of Liability</strong>
                <br />
                Motzu is not liable for any indirect, incidental, or consequential damages arising
                from your use of the Service. Our total liability is limited to the fees you paid
                for the Service in the 12 months before the claim, and a refund of those fees for
                the affected period is your sole and exclusive remedy.
              </Typography>
              <Typography variant="body2">
                <strong>7. Indemnification</strong>
                <br />
                You agree to defend, indemnify and hold Motzu harmless from claims arising from your
                use of the Service or its data, including prices you publish (for example, a
                manufacturer's MAP claim), your violation of these Terms or the law, and content you
                submit or connect. The full Terms are at /terms.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I have read and agree to the{' '}
                  <Link href="#" onClick={(e) => e.preventDefault()}>
                    Terms of Service
                  </Link>
                </Typography>
              }
            />
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3, mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Security color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Privacy Policy
              </Typography>
            </Box>
            <Box
              sx={{
                maxHeight: 200,
                overflowY: 'auto',
                bgcolor: 'grey.50',
                p: 2,
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Typography variant="body2" paragraph>
                <strong>1. Information We Collect</strong>
                <br />
                We collect information you provide during registration including your name, email,
                business information, and organization details. We also collect usage data to improve
                our services.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>2. How We Use Your Information</strong>
                <br />
                Your information is used to: (a) provide and maintain the Service; (b) communicate
                with you about your account; (c) send product updates and notifications; (d) improve
                our services.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>3. Data Security</strong>
                <br />
                We implement industry-standard security measures to protect your data. However, no
                method of transmission over the Internet is 100% secure.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>4. Third-Party Services</strong>
                <br />
                We may use third-party services for payment processing, analytics, and integrations.
                These services have their own privacy policies.
              </Typography>
              <Typography variant="body2">
                <strong>5. Your Rights</strong>
                <br />
                You have the right to access, correct, or delete your personal information. Contact
                us at privacy@motzu.com to exercise these rights.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I have read and agree to the{' '}
                  <Link href="#" onClick={(e) => e.preventDefault()}>
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={handleContinue}
            disabled={!canContinue || loading}
            sx={{ minWidth: 220, fontWeight: 600 }}
          >
            {loading ? 'Processing...' : 'Accept & Continue'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsAcceptancePage;
